import useAutoRefresh from "@/hooks/useAutoRefresh";
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import BracketView from "@/components/BracketView";
import DuprMatchPanel from "@/components/DuprMatchPanel";
import CoDirectorsPanel from "@/components/CoDirectorsPanel";
import TdPlayerManager from "@/components/TdPlayerManager";
import TdLotteryPanel from "@/components/TdLotteryPanel";
import PsiMoversBoard from "@/components/PsiMoversBoard";
import PlayerName from "@/components/PlayerName";
import { Pill, RatedBadge } from "@/components/perf";
import { Trophy, Calendar, MapPin, Users, ArrowLeft, Flag, Trash2, Crown, ClipboardCheck, Tv, UserCheck, RotateCcw, Merge, ChevronRight, Lightbulb, LayoutGrid, Clapperboard } from "lucide-react";
import { SetupReportModal } from "@/components/TournamentHub";
import { QRCodeSVG } from "qrcode.react";

const FORMAT_LABELS = {
  single_elim: "Single Elimination", double_elim: "Double Elimination",
  round_robin: "Round Robin", pool_play: "Pool Play → Playoffs",
};
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const PLACE_LABELS = { first: "1st Place", second: "2nd Place", third: "3rd Place" };
function PayoutRow({ place, amount, paid, prefill, tid, onDone }) {
  const [name, setName] = useState(prefill || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (prefill) setName((n) => n || prefill); }, [prefill]);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (paid) return (
    <div className="border border-green-500/50 bg-green-500/10 p-3" data-testid={`payout-paid-${place}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-black uppercase tracking-widest text-green-400">{PLACE_LABELS[place]} · Paid</span>
        <button data-testid={`payout-undo-${place}`} disabled={busy}
          onClick={() => run(() => api.delete(`/tournaments/${tid}/payouts/${place}`), "Payout removed from the Treasury log")}
          className="text-[9px] uppercase tracking-widest text-zinc-500 hover:text-red-400">Undo</button>
      </div>
      <div className="text-sm font-bold text-white">${paid.amount} → {paid.recipient}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">by {paid.paid_by} · {new Date(paid.paid_at).toLocaleDateString()}</div>
    </div>
  );
  return (
    <div className="border border-pb-border p-3" data-testid={`payout-row-${place}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">{PLACE_LABELS[place]}</span>
        <span className="text-sm font-bold text-yellow-400">${amount}</span>
      </div>
      <div className="flex gap-1.5">
        <input data-testid={`payout-name-${place}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Winner / team name"
          className="flex-1 min-w-0 bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white focus:outline-none focus:border-yellow-500" />
        <button data-testid={`payout-mark-${place}`} disabled={busy}
          onClick={() => { if (!name.trim()) return toast.error("Enter the winner's name first"); run(() => api.post(`/tournaments/${tid}/payouts`, { place, recipient: name }), `${PLACE_LABELS[place]} purse marked paid — logged to the Treasury`); }}
          className="bg-yellow-500 text-black text-[10px] font-black uppercase tracking-widest px-2.5 hover:bg-yellow-400 transition-colors disabled:opacity-50">Mark Paid</button>
      </div>
    </div>
  );
}

function BatchGenerate({ divisions, t, act, id, busy }) {
  const [sel, setSel] = useState([]);
  const [courtSel, setCourtSel] = useState([]);
  const [numPools, setNumPools] = useState(2);
  const [combineTarget, setCombineTarget] = useState("");
  const selectable = divisions.filter((d) => !d.has_matches && d.entry_count >= 1);
  const selDivs = selectable.filter((d) => sel.includes(d.label));
  const genDivs = selDivs.filter((d) => d.entry_count >= 2);
  const toggle = (label) => setSel((s) => s.includes(label) ? s.filter((x) => x !== label) : [...s, label]);
  const toggleCourt = (c) => setCourtSel((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c]);
  const go = () => {
    const groups = genDivs.map((d) => ({ event_type: d.event_type, division: d.division, age_bracket: d.age_bracket }));
    act(() => api.post(`/tournaments/${id}/generate-bracket`, { groups, courts: courtSel, per_pool: numPools }).then(() => { setSel([]); setCombineTarget(""); }),
      `${groups.length} bracket${groups.length > 1 ? "s" : ""} generated${courtSel.length ? ` on courts ${courtSel.join(", ")}` : " — courts split automatically"}`);
  };
  const combine = () => {
    const target = selDivs.find((d) => d.label === combineTarget);
    if (!target) return;
    const sources = selDivs.filter((d) => d.label !== combineTarget);
    if (!window.confirm(`Combine ${sources.map((s) => s.label).join(" + ")} into ${target.label}? They become ONE division with one bracket and one court assignment. Players will be notified.`)) return;
    act(async () => {
      for (const s of sources) {
        await api.post(`/tournaments/${id}/merge-divisions`, {
          from_event_type: s.event_type, from_age_bracket: s.age_bracket, from_division: s.division,
          to_event_type: target.event_type, to_age_bracket: target.age_bracket, to_division: target.division,
        });
      }
      setSel([]); setCombineTarget("");
    }, `Combined into ${target.label} — it's now one division playing as one unit`);
  };
  return (
    <div className="border border-pb-cyan/30 bg-pb-cyan/5 p-4 mb-6" data-testid="batch-generate-panel">
      <div className="text-[10px] uppercase tracking-[0.25em] text-pb-cyan font-bold mb-2">Generate Divisions — pick divisions, combine short-handed ones, then assign courts</div>
      <div className="flex flex-wrap gap-2 mb-2">
        {selectable.map((d) => (
          <label key={d.label} className={`flex items-center gap-1.5 border px-2.5 py-1.5 text-[11px] cursor-pointer transition-colors ${sel.includes(d.label) ? "border-pb-cyan text-pb-cyan bg-pb-cyan/10" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
            <input type="checkbox" data-testid={`batch-div-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              checked={sel.includes(d.label)} onChange={() => toggle(d.label)} className="accent-cyan-400" />
            {d.label} ({d.entry_count}){d.entry_count < 2 && <span className="text-amber-400 text-[9px]" title="Not enough entries to generate — combine it into another division">needs 2+</span>}
          </label>
        ))}
        {divisions.filter((d) => d.has_matches).map((d) => (
          <span key={d.label} data-testid={`batch-div-locked-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="flex items-center gap-2 border border-amber-500/40 bg-amber-500/5 px-2.5 py-1.5 text-[11px] text-amber-400/90">
            {d.label} ({d.entry_count}) <span className="text-[9px] uppercase tracking-wider">bracket exists</span>
            <button data-testid={`clear-bracket-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} disabled={busy}
              onClick={() => {
                if (!window.confirm(`Clear the existing ${d.label} bracket (all its recorded scores are erased) so you can regenerate it?`)) return;
                act(() => api.delete(`/tournaments/${id}/bracket`, { params: { event_type: d.event_type, age_bracket: d.age_bracket || undefined, division: d.division || undefined } }),
                  `${d.label} bracket cleared — it's now selectable for generation`);
              }}
              className="border border-amber-500/60 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest hover:bg-amber-500 hover:text-black transition-colors disabled:opacity-40">
              Clear
            </button>
          </span>
        ))}
      </div>
      {selDivs.length >= 2 && (
        <div className="flex flex-wrap items-center gap-2 border border-pb-border/60 bg-black/20 px-2.5 py-2 mb-2" data-testid="combine-row">
          <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Combine the {selDivs.length} selected into one:</span>
          <select data-testid="combine-target-select" value={combineTarget} onChange={(e) => setCombineTarget(e.target.value)}
            className="bg-pb-elevated border border-pb-border px-2 py-1.5 text-[11px] text-white focus:outline-none">
            <option value="">Keep as…</option>
            {selDivs.map((d) => <option key={d.label} value={d.label}>{d.label}</option>)}
          </select>
          <button data-testid="combine-selected-btn" disabled={busy || !combineTarget} onClick={combine}
            className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:bg-pb-cyan/10 active:scale-95 transition-all disabled:opacity-40">
            <Merge className="w-3.5 h-3.5" /> Combine
          </button>
          <span className="text-[9px] text-zinc-600">the combined group plays as one bracket on one set of courts</span>
        </div>
      )}
      {t.format === "pool_play" && (
        <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="batch-pool-controls">
          <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Pool play setup:</span>
          <select data-testid="batch-num-pools" value={numPools} onChange={(e) => setNumPools(Number(e.target.value))}
            className="bg-pb-elevated border border-pb-border px-2 py-1.5 text-[11px] text-white focus:outline-none">
            {[2, 3, 4].map((n) => <option key={n} value={n}>{n} pools per division</option>)}
          </select>
          <span className="text-[9px] text-zinc-600">(playoff size is picked later when pools finish)</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold">Their courts:</span>
        {(t.courts || []).map((c) => (
          <button key={c} type="button" data-testid={`batch-court-${c}`} aria-pressed={courtSel.includes(c)}
            onClick={() => toggleCourt(c)}
            className={`w-7 h-7 text-[11px] font-bold border transition-colors ${courtSel.includes(c) ? "border-pb-cyan bg-pb-cyan/15 text-pb-cyan" : "border-pb-border text-zinc-600 hover:border-zinc-500"}`}>
            {c}
          </button>
        ))}
        <span className="text-[9px] text-zinc-600">(none selected = courts split automatically)</span>
      </div>
      <button data-testid="batch-generate-btn" disabled={busy || genDivs.length === 0} onClick={go}
        className="bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-4 py-2 hover:bg-[#BEF264] transition-colors disabled:opacity-40">
        Generate {genDivs.length > 0 ? `${genDivs.length} ` : ""}Selected
      </button>
      {selDivs.length > 0 && genDivs.length === 0 && <span className="text-[10px] text-amber-400 ml-2">Selected divisions need at least 2 entries — combine them into a bigger one above.</span>}
    </div>
  );
}

function MedalCourtsPanel({ t, div, act, id, busy }) {
  const cfg = (t.medal_courts || {})[div.label] || {};
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(cfg.courts || []);
  const [champ, setChamp] = useState(cfg.championship || "");
  const [center, setCenter] = useState(cfg.center || "");
  const opts = sel.length ? sel : (t.courts || []);
  const save = () => act(() => api.put(`/tournaments/${id}/medal-courts`, {
    event_type: div.event_type, age_bracket: div.age_bracket, division: div.division,
    courts: sel, championship_court: champ ? Number(champ) : null, center_court: center ? Number(center) : null,
  }).then(() => setOpen(false)), "Medal courts saved — the gold medal match will only play on the championship court");
  if (!open) return (
    <button data-testid="medal-courts-toggle" onClick={() => setOpen(true)}
      className="flex items-center gap-1.5 border border-yellow-500/60 text-yellow-400 font-bold uppercase tracking-widest text-[10px] px-3 py-2.5 hover:bg-yellow-500/10 transition-colors">
      <Crown className="w-3.5 h-3.5" /> Medal Courts{cfg.championship ? ` — 🏆 Ct ${cfg.championship}` : ""}{cfg.center ? ` · ⭐ Ct ${cfg.center}` : ""}
    </button>
  );
  return (
    <div className="w-full border border-yellow-500/40 bg-yellow-500/5 px-3 py-2.5 flex flex-wrap items-center gap-2" data-testid="medal-courts-panel">
      <span className="text-[9px] uppercase tracking-widest text-yellow-500 font-bold">{div.label} playoff courts:</span>
      {(t.courts || []).map((c) => (
        <button key={c} type="button" data-testid={`medal-court-${c}`} aria-pressed={sel.includes(c)}
          onClick={() => setSel((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c])}
          className={`w-7 h-7 text-[11px] font-bold border transition-colors ${sel.includes(c) ? "border-yellow-500 bg-yellow-500/15 text-yellow-400" : "border-pb-border text-zinc-600 hover:border-zinc-500"}`}>
          {c}
        </button>
      ))}
      <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400">🏆 Gold on
        <select data-testid="medal-champ-select" value={champ} onChange={(e) => setChamp(e.target.value)}
          className="bg-pb-elevated border border-pb-border px-1.5 py-1.5 text-[11px] text-white focus:outline-none">
          <option value="">any court</option>
          {opts.map((c) => <option key={c} value={c}>Court {c}</option>)}
        </select>
      </label>
      <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400">⭐ Semis on
        <select data-testid="medal-center-select" value={center} onChange={(e) => setCenter(e.target.value)}
          className="bg-pb-elevated border border-pb-border px-1.5 py-1.5 text-[11px] text-white focus:outline-none">
          <option value="">any court</option>
          {opts.map((c) => <option key={c} value={c}>Court {c}</option>)}
        </select>
      </label>
      <button data-testid="medal-courts-save" disabled={busy} onClick={save}
        className="bg-yellow-500 text-black text-[10px] font-black uppercase tracking-widest px-3 py-1.5 hover:bg-yellow-400 transition-colors disabled:opacity-50">Save</button>
      <button data-testid="medal-courts-cancel" onClick={() => setOpen(false)}
        className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">Cancel</button>
    </div>
  );
}

function EditMyRegistration({ t, entries, user, act, id, busy }) {
  const mine = entries.find((e) => e.user_id === user?.id);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  if (!mine || t.status !== "open") return null;
  if (!open) return (
    <button data-testid="edit-my-reg-btn" onClick={() => { setF({ event_type: mine.event_type || "", division: mine.division || "", age_bracket: mine.age_bracket || "" }); setOpen(true); }}
      className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:border-pb-cyan hover:text-pb-cyan transition-colors mb-4">
      ✏️ Edit My Registration
    </button>
  );
  const sel = "bg-pb-elevated border border-pb-border px-2 py-2 text-xs text-white focus:outline-none";
  return (
    <div className="border border-pb-cyan/30 bg-pb-cyan/5 p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="edit-my-reg-panel">
      <span className="text-[9px] uppercase tracking-widest text-pb-cyan font-bold">My registration:</span>
      {(t.event_types || []).length > 1 && (
        <select data-testid="myreg-event" value={f.event_type} onChange={(e) => setF({ ...f, event_type: e.target.value })} className={sel}>
          {(t.event_types || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      )}
      {(t.levels || []).length > 1 && (
        <select data-testid="myreg-division" value={f.division} onChange={(e) => setF({ ...f, division: e.target.value })} className={sel}>
          {(t.levels || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      )}
      {(t.age_brackets || []).length > 0 && (
        <select data-testid="myreg-age" value={f.age_bracket} onChange={(e) => setF({ ...f, age_bracket: e.target.value })} className={sel}>
          <option value="">Any age</option>
          {(t.age_brackets || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      )}
      <button data-testid="myreg-save" disabled={busy}
        onClick={() => act(() => api.put(`/tournaments/${id}/my-registration`, {
          event_type: f.event_type || null, division: f.division || null, age_bracket: f.age_bracket ?? null,
        }).then(() => setOpen(false)), "Registration updated — your partner was notified")}
        className="bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:opacity-90 disabled:opacity-50">Save</button>
      <button data-testid="myreg-cancel" onClick={() => setOpen(false)} className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">Cancel</button>
      <span className="w-full text-[9px] text-zinc-600">Changes move your whole team and lock once brackets are generated — after that, ask the TD.</span>
    </div>
  );
}

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [perPool, setPerPool] = useState(2);
  const [topN, setTopN] = useState(2);
  const [tab, setTab] = useState(null);
  const [poCourts, setPoCourts] = useState([]);
  const [champCourt, setChampCourt] = useState("");
  const [centerCourt, setCenterCourt] = useState("");
  const [bronze, setBronze] = useState(false);
  useEffect(() => { setPoCourts([]); setChampCourt(""); setCenterCourt(""); setBronze(false); }, [tab]);
  const [subs, setSubs] = useState([]);
  const [qrCode, setQrCode] = useState(null);
  const [openCi, setOpenCi] = useState({});
  const [poFmt, setPoFmt] = useState({ quarters: 1, semis: 1, finals: 1 });
  const [showReport, setShowReport] = useState(false);
  const pfSaved = JSON.stringify(data?.tournament?.playoff_formats || null);
  useEffect(() => {
    const pf = JSON.parse(pfSaved);
    if (pf) setPoFmt({ quarters: pf.quarters || 1, semis: pf.semis || 1, finals: pf.finals || 1 });
  }, [pfSaved]);
  const selfCheckDone = useRef(false);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("checkin");
    if (!code || !user || selfCheckDone.current) return;
    selfCheckDone.current = true;
    api.post(`/tournaments/${id}/self-check-in`, { code })
      .then(({ data }) => {
        toast.success(`✓ You're checked in${data.events?.length ? ` for ${data.events.filter(Boolean).join(", ")}` : ""} — see you on the courts!`);
        window.history.replaceState({}, "", window.location.pathname);
        load();
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [user]); // eslint-disable-line

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tournaments/${id}`);
      setData(data);
      if (data.can_manage && data.matches.length > 0) {
        const r = await api.get(`/tournaments/${id}/score-submissions`);
        setSubs(r.data);
      }
    } catch { toast.error("Tournament not found"); navigate("/tournaments"); }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  if (user && user.role !== "admin" && !user.is_td) return <Navigate to="/tournaments" replace />;
  if (!data) return <div className="max-w-6xl mx-auto px-6 py-16 text-zinc-500 animate-pulse">Loading bracket…</div>;
  const { tournament: t, entries, matches, standings, can_manage, waitlist } = data;
  // one check-in row per TEAM: mutual partner entries collapse together
  const byUid = {};
  entries.forEach((e) => { if (e.user_id) byUid[e.user_id] = e; });
  const seenTeams = new Set();
  const checkinRows = [];
  for (const e of entries) {
    const mate = e.partner_id ? byUid[e.partner_id] : null;
    if (!(mate && mate.partner_id === e.user_id) && e.player_name && e.partner_name) {
      const nkey = ["nm", e.event_type, e.division, e.age_bracket, ...[e.player_name.toLowerCase(), e.partner_name.toLowerCase()].sort()].join("|");
      if (seenTeams.has(nkey)) continue;
      seenTeams.add(nkey);
    }
    if (mate && mate.partner_id === e.user_id) {
      const key = [e.id, mate.id].sort().join("-");
      if (seenTeams.has(key)) continue;
      seenTeams.add(key);
      checkinRows.push({
        ...e,
        members: [
          { id: e.id, user_id: e.user_id, name: e.player_name, checked_in: e.checked_in },
          { id: mate.id, user_id: mate.user_id, name: mate.player_name, checked_in: mate.checked_in },
        ],
        checked_in: e.checked_in && mate.checked_in,
        any_in: e.checked_in || mate.checked_in,
        on_hold: e.on_hold || mate.on_hold,
        paid: e.paid && mate.paid,
        pending_payment: e.pending_payment || mate.pending_payment,
        pending_entry_id: e.pending_payment ? e.id : mate.pending_payment ? mate.id : null,
      });
    } else {
      checkinRows.push({ ...e, pending_entry_id: e.pending_payment ? e.id : null });
    }
  }
  const divisions = data.divisions?.length ? data.divisions : [{ event_type: "Main", age_bracket: null, label: "Main", entry_count: entries.length }];
  const firstEv = divisions[0].event_type;
  const activeDiv = divisions.find((d) => d.label === tab) || divisions[0];
  const activeTab = activeDiv.label;
  const inDiv = (evField, abField, d, dvField) => (evField || firstEv) === d.event_type && ((abField || null) === (d.age_bracket || null)) && (!d.division || (dvField || null) === d.division);
  const nameOf = (eid) => entries.find((e) => e.id === eid)?.display_name || "—";

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const evEntries = checkinRows.filter((e) => inDiv(e.event_type, e.age_bracket, activeDiv, e.division));
  const evMatches = matches.filter((m) => inDiv(m.event_type, m.age_bracket, activeDiv, m.division));
  const evStandings = standings?.[activeTab] || {};
  const evAllPlayed = evMatches.length > 0 && evMatches.every((m) => m.status === "complete");
  const evHasFinals = evMatches.some((m) => ["playoff", "final"].includes(m.bracket));
  const evNeedsAdvance = ["round_robin", "pool_play"].includes(t.format) && evAllPlayed && !evHasFinals && t.status !== "complete";

  const anyNeedsAdvance = ["round_robin", "pool_play"].includes(t.format) && divisions.some((d) => {
    const ms = matches.filter((m) => inDiv(m.event_type, m.age_bracket, d, m.division));
    return ms.length > 0 && ms.every((m) => m.status === "complete") && !ms.some((m) => ["playoff", "final"].includes(m.bracket));
  });
  const allPlayed = matches.length > 0 && matches.every((m) => m.status === "complete");
  const canComplete = allPlayed && !anyNeedsAdvance && t.status !== "complete";
  const champs = t.champions && Object.keys(t.champions).length > 0 ? t.champions : null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="tournament-detail">
      <button data-testid="back-to-tournaments" onClick={() => navigate("/tournaments")}
        className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-pb-red mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> All Tournaments
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-display font-black text-3xl sm:text-4xl uppercase leading-none">{t.name}</h1>
            {t.demo && <Pill color="#22D3EE">DEMO — SANDBOX</Pill>}
            {t.status === "complete" && user?.role === "admin" && (
              <button data-testid="reopen-tournament-btn" disabled={busy}
                title="Rolls back medals, PBR points and player stat credit so you can correct scores, then Complete again"
                onClick={() => {
                  if (window.confirm("Reopen this completed tournament? Medals, PBR points and stat credit are rolled back until you hit Complete again after your corrections.")) {
                    act(() => api.post(`/tournaments/${id}/reopen`), "Reopened — make your corrections, then Complete the tournament again");
                  }
                }}
                className="flex items-center gap-1.5 border border-amber-400/60 text-amber-400 font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 hover:bg-amber-400 hover:text-black transition-all">
                <RotateCcw className="w-3 h-3" /> Reopen (Admin)
              </button>
            )}
            <Pill color={t.status === "complete" ? "#71717A" : t.status === "in_progress" ? "#22D3EE" : "#A3E635"}>
              {t.status === "in_progress" ? "Live" : t.status === "complete" ? "Final" : "Open"}
            </Pill>
            {t.status !== "complete" && (
              <a data-testid="court-board-link" href={`/court-board/${id}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 hover:bg-pb-cyan hover:text-black transition-all">
                <Tv className="w-3 h-3" /> Court Board
              </a>
            )}
            {t.status !== "complete" && can_manage && (
              <a data-testid="td-court-board-link" href={`/td-court-board/${id}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 border border-pb-red/60 text-pb-red font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 hover:bg-pb-red hover:text-black transition-all">
                <LayoutGrid className="w-3 h-3" /> TD Court Board
              </a>
            )}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400">
            <span className="flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5 text-pb-red" /> {FORMAT_LABELS[t.format]} · {(t.event_types || []).join(", ")}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-pb-red" /> {t.date}{t.end_date ? ` – ${t.end_date}` : ""}{t.start_time ? ` · ${t.start_time}${t.end_time ? `–${t.end_time}` : ""}` : ""}</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-pb-red" /> {t.location}</span>
            <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-pb-red" /> {entries.length}/{t.capacity}</span>
          </div>
          {t.event_config ? (
            <div className="text-xs text-zinc-500 mt-1 space-y-0.5" data-testid="td-event-config">
              {(t.event_types || []).map((ev) => {
                const c = t.event_config[ev] || {};
                return <div key={ev}><span className="text-zinc-300">{ev}:</span> {(c.levels || []).includes("All") ? "Open skill" : (c.levels || []).join(", ")}{c.age_brackets?.length ? ` · Ages ${c.age_brackets.join(", ")}` : ""}</div>;
              })}
              {t.play_style && <div>{t.play_style === "come_around" ? "Come Around" : "No Come Around"}</div>}
            </div>
          ) : (t.levels?.length > 0 || t.age_brackets?.length > 0) && (
            <div className="text-xs text-zinc-500 mt-1">{t.levels?.length > 0 ? `Levels: ${t.levels.join(", ")}` : ""}{t.age_brackets?.length > 0 ? ` · Ages: ${t.age_brackets.join(", ")}` : ""}{t.play_style ? ` · ${t.play_style === "come_around" ? "Come Around" : "No Come Around"}` : ""}</div>
          )}
          {t.video ? (
            <video src={t.video.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + t.video : t.video}
              controls autoPlay muted loop playsInline
              className="mt-3 w-full max-w-md max-h-64 bg-black border border-pb-border" data-testid="tournament-video" />
          ) : t.image && <img src={t.image.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + t.image : t.image} alt="" className="mt-3 h-28 w-full max-w-md object-cover border border-pb-border" data-testid="tournament-image" />}
          {t.description && <p className="text-sm text-zinc-500 mt-2 max-w-2xl">{t.description}</p>}
          {can_manage && t.announce_video?.status !== "ready" && (
            <div className="mt-3">
              {t.announce_video?.status === "generating" ? (
                <span data-testid="announce-video-generating" className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-pb-red border border-pb-red/40 px-3 py-2">
                  <span className="w-2 h-2 bg-pb-red animate-pulse" /> AI announcement video generating… ~2-5 min, it will appear here automatically
                </span>
              ) : (
                <button data-testid="announce-video-btn" disabled={busy}
                  title="Sora AI builds a 4-second cinematic announcement clip from your tournament name & location (uses AI credits)"
                  onClick={() => act(() => api.post(`/tournaments/${id}/announcement-video`), "Sora is on it — your announcement video will appear here in a few minutes")}
                  className="inline-flex items-center gap-1.5 border border-pb-red/60 text-pb-red text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:bg-pb-red/10 disabled:opacity-40">
                  <Clapperboard className="w-3.5 h-3.5" /> {t.announce_video?.status === "failed" ? "Retry Announcement Video (AI)" : "Generate Announcement Video (AI)"}
                </button>
              )}
              {t.announce_video?.status === "failed" && <div className="text-[10px] text-red-400 mt-1">Last attempt failed — {t.announce_video.error || "please try again"}</div>}
            </div>
          )}
        </div>
        {can_manage && (
          <div className="flex flex-wrap gap-2">
            {t.status === "in_progress" && matches.length > 0 && (
              <button data-testid="complete-btn" disabled={busy || !canComplete}
                title={canComplete ? "Crown the champions — medals, points and celebrations fire instantly"
                  : allPlayed && anyNeedsAdvance ? "Round play is done — generate the medal round first (or its scores)"
                  : `${matches.filter((m) => m.status !== "complete").length} match(es) still unplayed`}
                onClick={() => act(() => api.post(`/tournaments/${id}/complete`), "Tournament complete — champions crowned!")}
                className="flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                <Crown className="w-4 h-4" />
                {canComplete ? "Finish Tournament — Crown Champions"
                  : allPlayed && anyNeedsAdvance ? "Finish — Set Medal Round First"
                  : `Finish Tournament (${matches.filter((m) => m.status !== "complete").length} left)`}
              </button>
            )}
            <button data-testid="delete-tournament-btn" disabled={busy}
              onClick={() => { if (window.confirm("Delete this tournament and all its matches?")) act(() => api.delete(`/tournaments/${id}`).then(() => navigate("/tournaments")), "Tournament deleted"); }}
              className="flex items-center gap-2 border border-pb-border text-zinc-500 font-bold uppercase tracking-widest text-xs px-3 py-2.5 hover:border-red-500 hover:text-red-400 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {(t.prize_pools || []).length > 0 && (
        <div className="border border-yellow-500/40 bg-yellow-500/[0.04] p-5 mb-8" data-testid="tournament-prize-pools">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="text-yellow-500 font-display font-extrabold uppercase tracking-widest text-sm">💰 Moneyball Purse</span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">The purse grows as more players register</span>
            {Object.entries(t.team_dupr_caps || {}).map(([et, cap]) => (
              <span key={et} className="text-[10px] font-bold uppercase tracking-widest border border-yellow-500/40 text-yellow-400 px-2 py-0.5" data-testid={`tournament-team-cap-${et.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {et}: Team DUPR {cap} · PSI {(t.team_psi_caps || {})[et]}
              </span>
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {[...t.prize_pools].sort((a, b) => a.players - b.players).map((p) => {
              const unlocked = entries.length >= p.players;
              return (
                <div key={p.players} className={`border p-4 ${unlocked ? "border-yellow-500 bg-yellow-500/10" : "border-pb-border"}`} data-testid={`purse-tier-${p.players}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-black uppercase tracking-widest text-white">{p.players} Players</span>
                    <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 ${unlocked ? "bg-yellow-500 text-black" : "border border-pb-border text-zinc-500"}`}>
                      {unlocked ? "Unlocked" : `${Math.max(0, p.players - entries.length)} to go`}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span className="text-zinc-500 text-[10px] uppercase tracking-widest">1st Place</span><span className="font-bold text-yellow-400">${p.first}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500 text-[10px] uppercase tracking-widest">2nd Place</span><span className="font-bold text-white">${p.second}</span></div>
                    <div className="flex justify-between"><span className="text-zinc-500 text-[10px] uppercase tracking-widest">3rd Place</span><span className="font-bold text-white">${p.third}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
          {can_manage && (() => {
            const active = [...t.prize_pools].sort((a, b) => a.players - b.players).filter((p) => entries.length >= p.players).pop();
            const champ = champs ? Object.values(champs)[0] : null;
            const prefill = { first: champ?.champion_name || "", second: champ?.runner_up_name || "", third: "" };
            return (
              <div className="mt-4 border-t border-yellow-500/20 pt-4" data-testid="payout-tracker">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-yellow-500 mb-2">
                  Payout Tracker {active ? `— ${active.players}-player purse` : ""}
                  <span className="text-zinc-600 normal-case tracking-normal ml-2">every payout is logged to the Treasury</span>
                </div>
                {!active ? (
                  <p className="text-xs text-zinc-500" data-testid="payout-tracker-locked">No purse tier unlocked yet — payouts open once a player-count tier is reached.</p>
                ) : (
                  <div className="grid sm:grid-cols-3 gap-2">
                    {["first", "second", "third"].map((pl) => (
                      <PayoutRow key={pl} place={pl} amount={active[pl]} paid={(t.purse_payouts || {})[pl]} prefill={prefill[pl]} tid={id} onDone={load} />
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {showReport && <SetupReportModal tourney={t} onClose={() => setShowReport(false)} />}

      {t.status === "complete" && (champs || t.champion_id) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="champion-banner">
          {(champs ? Object.entries(champs) : [[divisions[0].label, { champion_id: t.champion_id, runner_up_id: t.runner_up_id }]]).map(([ev, c]) => (
            <div key={ev} className="border border-pb-red bg-pb-red/10 p-5 flex items-center gap-4" data-testid={`champion-${slug(ev)}`}>
              <Crown className="w-9 h-9 text-pb-red shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-pb-red font-bold">{ev} Champion</div>
                <div className="font-display font-black text-2xl uppercase">{c.champion_name || nameOf(c.champion_id)}</div>
                {(c.runner_up_name || c.runner_up_id) && <div className="text-xs text-zinc-400">2nd place: {c.runner_up_name || nameOf(c.runner_up_id)}</div>}
                {c.third_name && <div className="text-xs text-zinc-500">3rd place: {c.third_name}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
      {t.status === "complete" && t.psi_movers?.top3?.length > 0 && (
        <div className="mb-8"><PsiMoversBoard data={{ ...t.psi_movers, final: true }} title="Tournament Top 3 PSI Movers" testid="final-psi-movers" /></div>
      )}

      <EditMyRegistration t={t} entries={entries} user={user} act={act} id={id} busy={busy} />

      {can_manage && t.status !== "complete" && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="courts-selector">
          <button data-testid="setup-report-btn" onClick={() => setShowReport(true)}
            className="flex items-center gap-1.5 border border-amber-500/50 text-amber-400 font-bold uppercase tracking-widest text-[10px] px-3 py-2 hover:bg-amber-500/10 transition-colors">
            <Lightbulb className="w-3.5 h-3.5" /> Setup Report
          </button>
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Courts Available:</span>
          {Array.from({ length: 24 }, (_, i) => i + 1).map((c) => {
            const checked = (t.courts || []).includes(c);
            return (
              <button key={c} data-testid={`court-check-${c}`} aria-pressed={checked}
                onClick={() => act(() => api.put(`/tournaments/${id}/courts`, { courts: checked ? (t.courts || []).filter((x) => x !== c) : [...(t.courts || []), c] }))}
                className={`w-8 h-8 text-xs font-bold border transition-colors ${checked ? "border-green-500 bg-green-500/15 text-green-400" : "border-pb-border text-zinc-600 hover:border-zinc-500"}`}>
                {c}
              </button>
            );
          })}
          {(t.courts || []).length === 0 && <span className="text-xs text-amber-400">Check the courts you're using — required before generating a bracket.</span>}
        </div>
      )}

      {data.is_owner_td && (
        <CoDirectorsPanel id={id} coDirectors={data.co_directors} onChanged={load} />
      )}

      {can_manage && t.status !== "complete" && divisions.filter((d) => !d.has_matches && d.entry_count >= 1).length > 0 && (
        <BatchGenerate divisions={divisions} t={t} act={act} id={id} busy={busy} />
      )}

      {can_manage && t.status !== "complete" && entries.length > 0 && (
        <div className="border border-pb-border bg-pb-surface p-4 mb-6" data-testid="check-in-section">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-red font-bold">
              <UserCheck className="w-4 h-4" /> Player Check-In — {checkinRows.filter((e) => e.checked_in).length} of {checkinRows.length} here
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="launch-roster-btn" disabled={busy}
                onClick={() => act(async () => {
                  const { data: r } = await api.post(`/tournaments/${id}/roster/launch`);
                  if (r.roster_public) {
                    try { await navigator.clipboard.writeText(`${window.location.origin}/rosters/${id}`); } catch {}
                    toast.success("Public roster is LIVE — link copied to clipboard");
                  } else { toast.success("Public roster unpublished"); }
                })}
                className={`border font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 transition-colors ${t.roster_public ? "border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black" : "border-pb-border text-zinc-400 hover:border-lime-400 hover:text-lime-400"}`}>
                {t.roster_public ? "Roster LIVE — Unpublish" : "Launch Public Roster"}
              </button>
              {t.roster_public && (
                <a href={`/rosters/${id}`} target="_blank" rel="noreferrer" data-testid="view-public-roster-btn"
                  className="border border-pb-border text-zinc-400 font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:border-cyan-400 hover:text-cyan-300 transition-colors">
                  View
                </a>
              )}
              <button data-testid="print-roster-btn"
                onClick={() => {
                  const groups = divisions.map((d) => ({
                    label: d.label,
                    rows: checkinRows.filter((r) => (r.event_type || divisions[0]?.event_type) === d.event_type
                      && ((r.age_bracket || null) === (d.age_bracket || null))
                      && (!d.division || (r.division || null) === d.division)),
                  })).filter((g) => g.rows.length);
                  const w = window.open("", "_blank");
                  w.document.write(`<html><head><title>${t.name} — Registered Players</title>
                    <style>body{font-family:Georgia,serif;color:#111;padding:24px}h1{margin:0;font-size:20px;text-transform:uppercase}h2{font-size:13px;text-transform:uppercase;border-bottom:1px solid #999;padding-bottom:3px;margin:18px 0 6px}table{width:100%;border-collapse:collapse;font-size:12px}td,th{border:1px solid #bbb;padding:4px 6px;text-align:left}.meta{font-size:11px;color:#555;margin:4px 0 10px}</style></head><body>
                    <h1>${t.name} — Registered Players</h1>
                    <div class="meta">${t.date || ""} · ${t.location || ""} · ${checkinRows.length} teams / ${entries.length} entries · printed ${new Date().toLocaleString()}</div>
                    ${groups.map((g) => `<h2>${g.label} (${g.rows.length})</h2><table><tr><th>#</th><th>Team / Player</th><th>Checked In</th><th>Signature</th></tr>
                      ${g.rows.map((r, i) => `<tr><td>${i + 1}</td><td>${(r.members ? r.members.map((m) => m.name).join(" / ") : r.player_name) || ""}</td><td>${r.checked_in ? "YES" : ""}</td><td style="width:140px"></td></tr>`).join("")}</table>`).join("")}
                    </body></html>`);
                  w.document.close(); w.focus(); w.print();
                }}
                className="border border-pb-border text-zinc-400 font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:border-yellow-500 hover:text-yellow-400 transition-colors">
                Print / Download Roster
              </button>
              <button data-testid="self-checkin-qr-btn" disabled={busy}
                onClick={async () => {
                  if (qrCode) { setQrCode(null); return; }
                  try { const { data } = await api.get(`/tournaments/${id}/checkin-code`); setQrCode(data.code); }
                  catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}
                className="border border-pb-border text-zinc-400 font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:border-pb-cyan hover:text-pb-cyan transition-colors">
                {qrCode ? "Hide QR" : "Self Check-In QR"}
              </button>
              <button data-testid="check-in-all-btn" disabled={busy || entries.every((e) => e.checked_in)}
                onClick={() => act(() => api.post(`/tournaments/${id}/check-in-all`), "Everyone checked in")}
                className="border border-pb-border text-zinc-400 font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:border-green-500 hover:text-green-400 transition-colors disabled:opacity-40">
                Check In All
              </button>
            </div>
          </div>
          {qrCode && (
            <div className="flex flex-col sm:flex-row items-center gap-4 border border-pb-cyan/40 bg-pb-cyan/5 p-4 mb-3" data-testid="self-checkin-qr-panel">
              <div className="bg-white p-2.5 shrink-0">
                <QRCodeSVG value={`${window.location.origin}/tournaments/${id}?checkin=${qrCode}`} size={140} />
              </div>
              <div className="text-center sm:text-left">
                <div className="text-sm font-bold text-pb-cyan uppercase tracking-widest mb-1">Post this at the check-in desk</div>
                <p className="text-[11px] text-zinc-500 leading-relaxed">Players scan it with their phone camera — it opens this tournament and instantly marks them "here" (they just need to be signed in and registered). No line, no clipboard.</p>
                <button data-testid="self-checkin-copy-link"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/tournaments/${id}?checkin=${qrCode}`); toast.success("Check-in link copied"); }}
                  className="mt-2 text-[10px] uppercase tracking-widest font-bold text-pb-cyan underline">Copy link</button>
              </div>
            </div>
          )}
          <p className="text-[11px] text-zinc-500 mb-3">
            Check players in as they arrive — once anyone is checked in, brackets populate with who's actually here (no-shows are left out). If nobody is checked in, all registered entries are used.
          </p>
          <div className="space-y-2" data-testid="check-in-groups">
            {(() => {
              const ciGroups = {};
              for (const e of checkinRows) {
                const k = [e.event_type, e.division, e.age_bracket].filter(Boolean).join(" · ") || "General";
                (ciGroups[k] = ciGroups[k] || []).push(e);
              }
              const ciKeys = Object.keys(ciGroups).sort();
              return ciKeys.map((k) => {
                const rows = ciGroups[k];
                const here = rows.filter((e) => e.checked_in).length;
                const open = openCi[k] ?? ciKeys.length === 1;
                return (
                  <div key={k} className="border border-pb-border/60">
                    <button type="button" data-testid={`check-in-group-${k}`}
                      onClick={() => setOpenCi((p) => ({ ...p, [k]: !open }))}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${open ? "bg-pb-elevated/60" : "hover:bg-pb-elevated/40"}`}>
                      <ChevronRight className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open ? "rotate-90" : ""}`} />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-300 flex-1">{k}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${here === rows.length ? "text-green-400" : here > 0 ? "text-amber-400" : "text-zinc-600"}`}>
                        {here} of {rows.length} here
                      </span>
                    </button>
                    {open && (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 p-2 pt-0 mt-2">
                        {rows.map((e) => (
              <div key={e.id} data-testid={`check-in-row-${e.id}`}
                className={`flex items-center gap-2 border px-3 py-2 text-sm transition-colors ${e.on_hold ? "border-amber-500 bg-amber-500/10" : e.checked_in ? "border-green-500/50 bg-green-500/10" : e.any_in ? "border-amber-500/50 bg-amber-500/5" : "border-pb-border"}`}>
                {e.members ? (
                  <div className="flex-1 min-w-0 space-y-1.5">
                    {e.members.map((m) => (
                      <div key={m.id} className="flex items-center gap-2">
                        <span className="flex-1 min-w-0 truncate"><PlayerName userId={m.user_id} name={m.name} /></span>
                        <button data-testid={`check-in-btn-${m.id}`} disabled={busy} aria-pressed={m.checked_in}
                          onClick={() => act(() => api.post(`/tournaments/${id}/entries/${m.id}/check-in`, { checked_in: !m.checked_in }))}
                          className={`shrink-0 font-bold uppercase tracking-widest text-[9px] px-2.5 py-1 border transition-all active:scale-95 ${
                            m.checked_in ? "border-green-500 bg-green-500 text-black" : "border-pb-border text-zinc-400 hover:border-green-500 hover:text-green-400"}`}>
                          {m.checked_in ? "✓ Here" : "Check In"}
                        </button>
                      </div>
                    ))}
                    <span className="flex items-center gap-1 text-[10px] text-zinc-500">{e.division || e.event_type || ""}{e.paid ? " · paid" : ""}{e.on_hold ? <span className="text-amber-400 font-bold"> · ON HOLD</span> : null}{e.pending_payment ? <span className="text-amber-400"> · payment pending</span> : null}
                      {e.pending_entry_id && (
                        <button data-testid={`remove-entry-${e.pending_entry_id}`} disabled={busy} title="Remove unpaid entry"
                          onClick={() => act(() => api.delete(`/tournaments/${id}/entries/${e.pending_entry_id}`))}
                          className="text-zinc-500 hover:text-pb-red font-bold px-1">✕</button>
                      )}
                      {t.status !== "open" && (
                        <button data-testid={`hold-btn-${e.id}`} disabled={busy} title={e.on_hold ? "Lift the hold — put their matches back in the court queue" : "Running late — skip their matches in the court queue without touching the bracket"}
                          onClick={() => act(() => api.post(`/tournaments/${id}/entries/${e.id}/hold`, { hold: !e.on_hold }), e.on_hold ? "Hold lifted — their matches are back in the rotation" : "Team on hold — courts will skip them until they arrive")}
                          className={`ml-auto shrink-0 font-bold uppercase tracking-widest text-[9px] px-2 py-0.5 border transition-all active:scale-95 ${
                            e.on_hold ? "border-amber-400 bg-amber-400 text-black" : "border-pb-border text-zinc-500 hover:border-amber-400 hover:text-amber-400"}`}>
                          {e.on_hold ? "▶ They're Here" : "⏸ Late"}
                        </button>
                      )}
                    </span>
                  </div>
                ) : (
                  <>
                    <span className="flex-1 min-w-0 truncate"><PlayerName userId={e.user_id} name={e.display_name} />
                      <span className="block text-[10px] text-zinc-500">{e.division || e.event_type || ""}{e.paid ? " · paid" : ""}{e.on_hold ? <span className="text-amber-400 font-bold"> · ON HOLD</span> : null}{e.pending_payment ? <span className="text-amber-400"> · payment pending</span> : null}</span>
                    </span>
                    {e.pending_entry_id && (
                      <button data-testid={`remove-entry-${e.pending_entry_id}`} disabled={busy} title="Remove unpaid entry"
                        onClick={() => act(() => api.delete(`/tournaments/${id}/entries/${e.pending_entry_id}`))}
                        className="shrink-0 text-zinc-500 hover:text-pb-red font-bold px-1.5">✕</button>
                    )}
                    {t.status !== "open" && (
                      <button data-testid={`hold-btn-${e.id}`} disabled={busy} title={e.on_hold ? "Lift the hold — put their matches back in the court queue" : "Running late — skip their matches in the court queue without touching the bracket"}
                        onClick={() => act(() => api.post(`/tournaments/${id}/entries/${e.id}/hold`, { hold: !e.on_hold }), e.on_hold ? "Hold lifted — their matches are back in the rotation" : "Team on hold — courts will skip them until they arrive")}
                        className={`shrink-0 font-bold uppercase tracking-widest text-[9px] px-2 py-1.5 border transition-all active:scale-95 ${
                          e.on_hold ? "border-amber-400 bg-amber-400 text-black" : "border-pb-border text-zinc-500 hover:border-amber-400 hover:text-amber-400"}`}>
                        {e.on_hold ? "▶ Here" : "⏸ Late"}
                      </button>
                    )}
                    <button data-testid={`check-in-btn-${e.id}`} disabled={busy} aria-pressed={e.checked_in}
                      onClick={() => act(() => api.post(`/tournaments/${id}/entries/${e.id}/check-in`, { checked_in: !e.checked_in }))}
                      className={`shrink-0 font-bold uppercase tracking-widest text-[9px] px-2.5 py-1.5 border transition-all active:scale-95 ${
                        e.checked_in ? "border-green-500 bg-green-500 text-black" : "border-pb-border text-zinc-400 hover:border-green-500 hover:text-green-400"}`}>
                      {e.checked_in ? "✓ Here" : "Check In"}
                    </button>
                  </>
                )}
              </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {can_manage && t.status !== "complete" && (
        <TdPlayerManager tid={id} entries={entries} waitlist={waitlist} onChanged={load} levels={t.levels} ageBrackets={t.age_brackets} eventTypes={t.event_types} eventConfig={t.event_config} />
      )}
      {can_manage && t.status === "open" && (
        <div className="mt-6 border border-pb-cyan/40 bg-pb-cyan/5 p-4 flex flex-wrap items-center gap-3" data-testid="td-public-signup-link">
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-[0.25em] text-pb-cyan font-bold">Public sign-up link · no account needed</div>
            <div className="font-mono text-xs text-white break-all mt-1" data-testid="td-public-signup-url">{window.location.origin}/toursignup?t={id}</div>
            <div className="text-[11px] text-zinc-500 mt-1">Post it anywhere — players create their account, pick events, sign the waiver and pay in one flow.</div>
          </div>
          <button data-testid="td-copy-signup-link" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/toursignup?t=${id}`); toast.success("Sign-up link copied"); }}
            className="bg-pb-cyan text-black font-black uppercase tracking-widest text-[10px] px-4 py-2.5 hover:bg-cyan-300 active:scale-95 transition-all">Copy link</button>
        </div>
      )}
      {can_manage && <TdLotteryPanel tid={id} />}

      {can_manage && subs.length > 0 && (
        <div className="border border-amber-500/50 bg-amber-500/5 p-4 mb-6" data-testid="score-approvals">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-400 font-bold mb-3">
            <ClipboardCheck className="w-4 h-4" /> Player Score Submissions — Awaiting Your Approval ({subs.length} {subs.length === 1 ? "match" : "matches"})
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {subs.map((g) => (
              <div key={g.match_key} data-testid={`score-group-${g.match_key}`}
                className={`bg-pb-surface border p-3 ${g.agree ? "border-pb-red/60" : g.conflict ? "border-red-500/60" : "border-pb-border"}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {g.court && (
                    <span className="bg-amber-400 text-black font-black font-display uppercase text-xs px-2 py-0.5" data-testid={`group-court-${g.match_key}`}>
                      Court {g.court}
                    </span>
                  )}
                  <span className="font-mono text-xs text-zinc-500">{g.match_key}</span>
                  {g.event_type && <span className="text-[9px] uppercase tracking-widest text-zinc-500">{g.event_type}</span>}
                  <span className="ml-auto text-xs text-zinc-400 truncate">{g.team_a} <span className="text-zinc-600">vs</span> {g.team_b}</span>
                </div>
                <div className="space-y-1.5">
                  {g.submissions.map((s) => (
                    <div key={s.id} data-testid={`score-sub-${s.id}`} className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="flex-1 min-w-[160px]"><span className="font-semibold">{s.team}</span> reports <span className="font-bold text-amber-300">{s.score_for}–{s.score_against}</span></span>
                      {!g.agree && (
                        <>
                          <button data-testid={`approve-sub-${s.id}`} disabled={busy}
                            onClick={() => act(() => api.post(`/tournaments/${id}/score-submissions/${s.id}/approve`), "Score approved — bracket updated & courts reassigned")}
                            className="bg-pb-red text-black font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
                            Approve
                          </button>
                          <button data-testid={`reject-sub-${s.id}`} disabled={busy}
                            onClick={() => act(() => api.delete(`/tournaments/${id}/score-submissions/${s.id}`), "Submission rejected — the player was notified")}
                            className="border border-pb-border text-zinc-400 font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50">
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {g.agree && (
                  <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-pb-border/50" data-testid={`group-agree-${g.match_key}`}>
                    <span className="text-xs text-pb-red font-bold uppercase tracking-widest">✓ Both teams agree</span>
                    <button data-testid={`approve-group-${g.match_key}`} disabled={busy}
                      onClick={() => act(() => api.post(`/tournaments/${id}/score-submissions/${g.submissions[0].id}/approve`), "Score approved — bracket updated & courts reassigned")}
                      className="ml-auto bg-pb-red text-black font-bold uppercase tracking-widest text-[10px] px-4 py-1.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
                      Approve {g.submissions[0].score_for}–{g.submissions[0].score_against}
                    </button>
                  </div>
                )}
                {g.conflict && (
                  <div className="mt-2 pt-2 border-t border-red-500/30 text-[10px] uppercase tracking-widest text-red-400 font-bold" data-testid={`group-conflict-${g.match_key}`}>
                    ⚠ Scores conflict — approve the correct one
                  </div>
                )}
                {!g.agree && !g.conflict && (
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-zinc-600" data-testid={`group-waiting-${g.match_key}`}>
                    Waiting on the other team's report — you can approve now if it looks right
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {divisions.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="event-tabs">
          {divisions.map((d) => {
            const active = d.label === activeTab;
            return (
              <button key={d.label} data-testid={`event-tab-${slug(d.label)}`} onClick={() => setTab(d.label)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border transition-colors ${
                  d.playoff_ready ? `border-red-500 bg-red-500/15 text-red-300 shadow-[0_0_12px_rgba(239,68,68,0.5)] animate-pulse ${active ? "text-white" : ""}`
                  : active ? "border-pb-red bg-pb-red/10 text-white" : "border-pb-border text-zinc-500 hover:border-zinc-600"}`}
                title={d.playoff_ready ? "All regular-play matches are complete — this division is ready for playoffs" : undefined}>
                {d.label} {d.playoff_ready && <span className="text-[8px] font-black text-red-400" data-testid={`playoff-ready-${slug(d.label)}`}>● PLAYOFF READY</span>} {d.orphan && <span className="text-amber-400 text-[8px]" title="These players signed up under a bracket that no longer exists — use the age/division dropdowns in Team Rosters to move them">⚠ old bracket</span>} <span className={active ? "text-pb-red" : "text-zinc-600"}>({d.entry_count})</span>
              </button>
            );
          })}
        </div>
      )}

      {can_manage && t.status !== "complete" && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="event-controls">
          {evMatches.length > 0 && t.status !== "complete" && (
            <button data-testid="regenerate-bracket-btn" disabled={busy}
              onClick={() => {
                if (!window.confirm(`Delete the ${activeTab} bracket? All recorded scores in this division are erased so you can adjust entries/check-ins and generate a fresh bracket.`)) return;
                act(() => api.delete(`/tournaments/${id}/bracket`, { params: { event_type: activeDiv.event_type, age_bracket: activeDiv.age_bracket || undefined, division: activeDiv.division || undefined } }),
                  "Bracket deleted — adjust and generate a new one");
              }}
              className="flex items-center gap-1.5 border border-amber-500/60 text-amber-400 font-bold uppercase tracking-widest text-[10px] px-3 py-2.5 hover:bg-amber-500/10 active:scale-95 transition-all disabled:opacity-40">
              <RotateCcw className="w-3.5 h-3.5" /> Regenerate {divisions.length > 1 ? `${activeTab} Bracket` : "Bracket"}
            </button>
          )}
          {evMatches.length > 0 && (["single_elim", "double_elim"].includes(t.format) || evHasFinals) && (
            <MedalCourtsPanel key={activeTab} t={t} div={activeDiv} act={act} id={id} busy={busy} />
          )}
          {evHasFinals && ["round_robin", "pool_play"].includes(t.format) && t.status !== "complete" && (
            <button data-testid="regenerate-playoffs-btn" disabled={busy}
              title="Wipe this division's playoff bracket (scores included) and set it up again with new seeds, format or courts"
              onClick={() => {
                if (window.confirm(`Clear the ${divisions.length > 1 ? `${activeTab} ` : ""}playoff bracket? Playoff matches and their scores are deleted — round play results are kept — then you set the playoffs up again.`)) {
                  act(() => api.delete(`/tournaments/${id}/playoffs`, { params: { event_type: activeDiv.event_type, age_bracket: activeDiv.age_bracket || undefined, division: activeDiv.division || undefined } }),
                    "Playoffs cleared — set them up again below");
                }
              }}
              className="flex items-center gap-1.5 border border-red-500/60 text-red-400 font-bold uppercase tracking-widest text-[10px] px-3 py-2.5 hover:bg-red-500/10 active:scale-95 transition-all disabled:opacity-40">
              <RotateCcw className="w-3.5 h-3.5" /> Regenerate {divisions.length > 1 ? `${activeTab} Playoffs` : "Playoffs"}
            </button>
          )}
          {evNeedsAdvance && (
            <>
              {t.format === "pool_play" && (() => {
                const nPools = Object.keys(evStandings).length || 2;
                return (
                  <select data-testid="per-pool" value={perPool} onChange={(e) => setPerPool(Number(e.target.value))}
                    className="bg-pb-elevated border border-pb-border px-2 py-2.5 text-sm text-white focus:outline-none">
                    {[1, 2, 3, 4].map((n) => (
                      <option key={n} value={n}>Top {n}/pool{nPools ? ` — ${n * nPools}-team playoff` : ""}</option>
                    ))}
                    <option value={0}>Everyone makes playoffs — all teams seeded by pool finish</option>
                  </select>
                );
              })()}
              {t.format === "round_robin" && (
                <select data-testid="top-n" value={topN} onChange={(e) => setTopN(Number(e.target.value))}
                  className="bg-pb-elevated border border-pb-border px-2 py-2.5 text-sm text-white focus:outline-none">
                  {[{ n: 2, l: "Top 2 — Final only" }, { n: 4, l: "Top 4 — Semis + Final" },
                    { n: 6, l: "Top 6 — Quarters (top 2 byes)" }, { n: 8, l: "Top 8 — Quarterfinals" }]
                    .filter((o) => o.n <= evEntries.length)
                    .map((o) => <option key={o.n} value={o.n}>{o.l}</option>)}
                  <option value={0}>Everyone makes playoffs — all {evEntries.length} seeded by standings</option>
                </select>
              )}
              <div className="flex items-center gap-1.5" data-testid="playoff-format-picker">
                {[["quarters", "QF", [1, 3]], ["semis", "SF", [1, 3]], ["finals", "Final", [1, 3, 5]]].map(([k, label, opts]) => (
                  <label key={k} className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500">
                    {label}
                    <select data-testid={`po-fmt-${k}`} value={poFmt[k] || 1}
                      onChange={(e) => setPoFmt((p) => ({ ...p, [k]: Number(e.target.value) }))}
                      className="bg-pb-elevated border border-pb-border px-1.5 py-2 text-[11px] text-white focus:outline-none">
                      {opts.map((n) => <option key={n} value={n}>{n === 1 ? "1 game" : `Bo${n}`}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <div className="w-full flex flex-wrap items-center gap-2 border border-yellow-500/40 bg-yellow-500/5 px-3 py-2.5" data-testid="playoff-courts-setup">
                <span className="text-[9px] uppercase tracking-widest text-yellow-500 font-bold">Playoff courts:</span>
                {(t.courts || []).map((c) => (
                  <button key={c} type="button" data-testid={`po-court-${c}`} aria-pressed={poCourts.includes(c)}
                    onClick={() => setPoCourts((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c])}
                    className={`w-7 h-7 text-[11px] font-bold border transition-colors ${poCourts.includes(c) ? "border-yellow-500 bg-yellow-500/15 text-yellow-400" : "border-pb-border text-zinc-600 hover:border-zinc-500"}`}>
                    {c}
                  </button>
                ))}
                <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400">🏆 Gold on
                  <select data-testid="champ-court-select" value={champCourt} onChange={(e) => setChampCourt(e.target.value)}
                    className="bg-pb-elevated border border-pb-border px-1.5 py-1.5 text-[11px] text-white focus:outline-none">
                    <option value="">any court</option>
                    {(poCourts.length ? poCourts : (t.courts || [])).map((c) => <option key={c} value={c}>Court {c}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400">⭐ Semis/Bronze on
                  <select data-testid="center-court-select" value={centerCourt} onChange={(e) => setCenterCourt(e.target.value)}
                    className="bg-pb-elevated border border-pb-border px-1.5 py-1.5 text-[11px] text-white focus:outline-none">
                    <option value="">any court</option>
                    {(poCourts.length ? poCourts : (t.courts || [])).map((c) => <option key={c} value={c}>Court {c}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-300 cursor-pointer">
                  <input type="checkbox" data-testid="bronze-match-check" checked={bronze} onChange={(e) => setBronze(e.target.checked)} className="accent-yellow-500" />
                  Bronze medal match (semifinal losers play for 3rd)
                </label>
                <span className="w-full text-[9px] text-zinc-600">Leave courts unchecked to keep this division's current courts. The gold medal match plays ONLY on the championship court; semis and the bronze match go to center court first.</span>
              </div>
              <button data-testid="advance-btn" disabled={busy}
                onClick={() => act(() => api.post(`/tournaments/${id}/advance`, { event_type: activeDiv.event_type, age_bracket: activeDiv.age_bracket, division: activeDiv.division, per_pool: perPool || 2, top_n: topN || 2, everyone: t.format === "pool_play" ? perPool === 0 : topN === 0, playoff_formats: poFmt, playoff_courts: poCourts, championship_court: champCourt ? Number(champCourt) : null, center_court: centerCourt ? Number(centerCourt) : null, bronze_match: bronze }), `${activeTab} finals set!`)}
                className="flex items-center gap-2 bg-pb-cyan text-black font-bold uppercase tracking-widest text-xs px-4 py-2.5 active:scale-95 transition-all disabled:opacity-50">
                <Flag className="w-4 h-4" /> Set {divisions.length > 1 ? `${activeTab} Finals` : "Finals"}
              </button>
              {t.format === "round_robin" && (
                <span className="text-[11px] text-zinc-500">or skip playoffs — press Finish and the standings decide the champion.</span>
              )}
            </>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-pb-surface border border-pb-border p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">{divisions.length > 1 ? `${activeTab} Entries` : "Entries"} ({evEntries.length})</div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="entries-list">
              {evEntries.length === 0 && <div className="text-xs text-zinc-600">No entries yet.</div>}
              {evEntries.map((e, i) => (
                <div key={e.id} className="text-sm flex items-center gap-2">
                  <span className="text-zinc-600 font-mono text-xs w-5">{i + 1}.</span>
                  <PlayerName userId={e.user_id} name={e.display_name} className="truncate" testId={`entry-name-${e.id}`} />
                  <RatedBadge dupr={e.dupr} vair={e.vair} />
                  {e.pending_payment ? (
                    <span className="text-[8px] uppercase tracking-widest text-amber-400 border border-amber-400/40 px-1" data-testid={`entry-pending-${e.id}`}>Pending</span>
                  ) : e.paid && <span className="text-[8px] uppercase tracking-widest text-pb-red border border-pb-red/40 px-1">Paid</span>}
                  {e.vip && <span className="text-[8px] uppercase tracking-widest text-yellow-300 border border-yellow-400/50 bg-yellow-400/10 px-1" data-testid={`entry-vip-${e.id}`}>★ VIP</span>}
                </div>
              ))}
            </div>
          </div>
          {Object.entries(evStandings).map(([label, rows]) => (
            <div key={label} className="bg-pb-surface border border-pb-border p-5" data-testid={`standings-${label}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">{label === "rr" ? "Standings" : `Pool ${label} Standings`}</div>
              <table className="w-full text-xs">
                <thead><tr className="text-zinc-600 text-left"><th className="pb-1">Team</th><th className="text-center">W</th><th className="text-center">L</th><th className="text-right">+/-</th></tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.entry_id} className={i < 2 ? "text-pb-red" : "text-zinc-300"}>
                      <td className="py-1 truncate max-w-[120px]">{nameOf(r.entry_id)}</td>
                      <td className="text-center font-mono">{r.wins}</td>
                      <td className="text-center font-mono">{r.losses}</td>
                      <td className="text-right font-mono">{r.pf - r.pa > 0 ? "+" : ""}{r.pf - r.pa}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        <div className="lg:col-span-3">
          {evMatches.length === 0 ? (
            <div className="border border-pb-border bg-pb-surface p-10 text-center text-zinc-500 text-sm" data-testid="no-bracket">
              {can_manage
                ? `No ${divisions.length > 1 ? `${activeTab} ` : ""}bracket yet — use the Generate Divisions box above when registration is done. The field locks once you do.`
                : "Bracket hasn't been generated yet. Check back soon."}
            </div>
          ) : (
            <BracketView tournamentId={id} matches={evMatches} entries={entries} canManage={can_manage && t.status !== "complete"} onChange={load} seeds={t.playoff_seeds || {}} />
          )}
          {can_manage && t.dupr_rated && <DuprMatchPanel tournamentId={id} matches={matches} />}
        </div>
      </div>

      {(can_manage || data.can_observe) && matches.length > 0 && (
        <div className="mt-10 grid sm:grid-cols-2 gap-3">
          <a href={`/court-layout/${id}`} target="_blank" rel="noreferrer" data-testid="open-court-layout-btn"
            className="flex items-center justify-center gap-3 border border-[#A3E635]/60 bg-[#A3E635]/5 text-[#A3E635] font-display font-black uppercase tracking-widest text-sm px-6 py-4 hover:bg-[#A3E635] hover:text-black transition-all">
            <LayoutGrid className="w-5 h-5" /> Full Court Layout — live
          </a>
          <a href={`/tournaments/${id}/report`} target="_blank" rel="noreferrer" data-testid="open-report-btn"
            className="flex items-center justify-center gap-3 border border-pb-cyan/60 bg-pb-cyan/5 text-pb-cyan font-display font-black uppercase tracking-widest text-sm px-6 py-4 hover:bg-pb-cyan hover:text-black transition-all">
            <ClipboardCheck className="w-5 h-5" /> Tournament Report — print &amp; download
          </a>
        </div>
      )}
    </div>
  );
}
